'use client';

import type { Route } from 'next';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useMemo } from 'react';
import { useUser } from '@/contexts/user-context';

const LOCALE_PREFIX_REGEX = /^\/(en|pt|es)(?=\/|$)/;
const ALWAYS_ALLOWED_PREFIXES = ['/api', '/_next', '/home'];
const ALWAYS_ALLOWED_PATHS: Record<string, true> = {
  '/': true,
  '/home': true,
  '/offer': true,
  '/login': true,
  '/verify-email': true,
  '/forgot-password': true,
  '/reset-password': true,
  '/privacy': true,
  '/terms': true,
  '/verified': true,
};

function isAllowedPath(pathname: string) {
  const pathnameWithoutLocale = pathname.replace(LOCALE_PREFIX_REGEX, '') || '/';
  if (pathnameWithoutLocale in ALWAYS_ALLOWED_PATHS) {
    return true;
  }
  return ALWAYS_ALLOWED_PREFIXES.some((prefix) => pathnameWithoutLocale.startsWith(prefix));
}

export function HardAccessGate({ children }: { children: React.ReactNode }) {
  const { userProgress } = useUser();
  const pathname = usePathname() || '/';
  const router = useRouter();
  const localePrefix = pathname.match(LOCALE_PREFIX_REGEX)?.[0] ?? '';

  const shouldBlock = useMemo(() => {
    if (!userProgress) {
      return false;
    }
    if (userProgress.role === 'admin') {
      return false;
    }
    const isHard = userProgress.paywallVariant === 'hard';
    const isFree = (userProgress.subscriptionStatus ?? 'stargazer') === 'stargazer';
    if (!(isHard && isFree)) {
      return false;
    }
    return !isAllowedPath(pathname);
  }, [pathname, userProgress]);

  useEffect(() => {
    if (shouldBlock) {
      router.push(`${localePrefix}/offer` as Route);
    }
  }, [localePrefix, router, shouldBlock]);

  if (shouldBlock) {
    return null;
  }

  return children;
}
